import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import {
  Database,
  List,
  Layers,
  Building2,
  Layout,
  ScrollText,
  Settings,
  Grid,
} from "lucide-react";
import { HotelAccounts } from "./internal/HotelAccounts";
import { SaasAccounts } from "./internal/SaasAccounts";
import { InternalLogs } from "./internal/InternalLogs";
import { PMSTable } from "./internal/PMSTable";
import { PMSScopes } from "./internal/PMSScopes";
import { PMSIntegrationsGrid } from "./internal/PMSIntegrationsGrid";
import { InternalSettings } from "./internal/InternalSettings";
import { UserMenu } from "../UserMenu";
import { ThemeToggle } from "../ThemeToggle";
import { ThemeProvider } from "../../contexts/ThemeContext";

export function InternalExtranet() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState("pms");
  const [activePMSSection, setActivePMSSection] = useState("list");
  const [userEmail, setUserEmail] = useState("");

  useEffect(() => {
    const getUser = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        setUserEmail(user.email || "");
      }
    };
    getUser();

    // Check if we should show settings with user tab
    if (location.state?.settingsTab === "user") {
      setActiveSection("settings");
    }
  }, [location.state]);

  const renderContent = () => {
    if (activeSection === "pms") {
      return (
        <div className="extranet-container">
          <div className="extranet-container-header">
            <div className="flex items-center">
              <Database className="h-6 w-6 text-indigo-600 mr-2" />
              <h2 className="text-2xl font-bold text-gray-900">Connectors</h2>
            </div>
          </div>

          <div className="extranet-container-body">
            {/* Tabs */}
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setActivePMSSection("list")}
                  className={`
                    flex items-center py-4 px-1 border-b-2 font-medium text-sm
                    ${
                      activePMSSection === "list"
                        ? "border-indigo-500 text-indigo-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }
                  `}
                >
                  <List className="h-5 w-5 mr-2" />
                  Connectors List
                </button>
                <button
                  onClick={() => setActivePMSSection("scopes")}
                  className={`
                    flex items-center py-4 px-1 border-b-2 font-medium text-sm
                    ${
                      activePMSSection === "scopes"
                        ? "border-indigo-500 text-indigo-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }
                  `}
                >
                  <Layers className="h-5 w-5 mr-2" />
                  Connector Scopes
                </button>
              </nav>
            </div>

            {/* Content */}
            <div className="mt-6">
              {activePMSSection === "list" ? <PMSTable /> : <PMSScopes />}
            </div>
          </div>
        </div>
      );
    }

    switch (activeSection) {
      case "pms_integrations":
        return <PMSIntegrationsGrid />;
      case "hotels":
        return <HotelAccounts />;
      case "saas":
        return <SaasAccounts />;
      case "logs":
        return <InternalLogs />;
      case "settings":
        return <InternalSettings />;
      default:
        return <PMSTable />;
    }
  };

  const menuItems = [
    { id: "pms", label: "Connectors", icon: Database },
    { id: "pms_integrations", label: "PMS Integrations", icon: Grid },
    { id: "hotels", label: "Hotel Accounts", icon: Building2 },
    { id: "saas", label: "SaaS Accounts", icon: Layout },
    { id: "logs", label: "System Logs", icon: ScrollText },
    { id: "settings", label: "Settings", icon: Settings },
  ];

  return (
    <ThemeProvider>
      <div className="extranet-layout">
        <div className="extranet-sidebar">
          {/* Logo */}
          <div className="p-4">
            <div className="flex flex-col">
              <div className="w-32">
                <img
                  src="/images/omnylink_logo.png"
                  alt="Omnylink"
                  className="w-full h-auto block"
                />
                <img
                  src="/images/omnylink_logo_white.png"
                  alt="Omnylink"
                  className="w-full h-auto hidden"
                />
              </div>
              <span className="mt-1 text-sm font-medium text-gray-600">
                Internal Dashboard
              </span>
            </div>
          </div>

          {/* Navigation Menu */}
          <nav className="flex-1 p-4 space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className={`
                    w-full flex items-center px-3 py-2 text-sm font-medium rounded-md
                    ${
                      activeSection === item.id
                        ? "bg-indigo-50"
                        : "text-gray-600"
                    }
                  `}
                >
                  <Icon
                    className={`
                    h-5 w-5 mr-3
                    ${
                      activeSection === item.id
                        ? "text-indigo-500"
                        : "text-gray-400"
                    }
                  `}
                  />
                  {item.label}
                </button>
              );
            })}
          </nav>

          {/* User Menu and Theme Toggle */}
          <div className="p-4 flex items-center justify-between">
            <UserMenu email={userEmail} />
            <ThemeToggle />
          </div>
        </div>

        {/* Main Content */}
        <div className="extranet-content">
          <div className="extranet-content-inner">{renderContent()}</div>
        </div>
      </div>
    </ThemeProvider>
  );
}
